import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faMedal, faUsers, faCalendarDays, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import Participants from '../components/Competitions/Participants';
import WallOfFame from '../components/Awards/WallOfFame';
import './DemoShowcase.css';

const API = process.env.REACT_APP_API_BASE_URL;
const day = (s, opts = { month: 'long', day: 'numeric' }) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', opts) : '';
};
const PLACE = { 1: '1st', 2: '2nd', 3: '3rd' };

/* Free-trial dashboard: "Life at RoboMind"
   - this month's challenge (view only: who takes part, or the winners) + "Enroll to join"
   - the Wall of fame (badges and medals our students earn)
   Both come from /api/public/showcase (free-trial students have no login token). */
const DemoShowcase = ({ waUrl, firstName }) => {
  const [comp, setComp] = useState(undefined);
  const [feed, setFeed] = useState(null);

  // free-trial students have no login token: public endpoint (titles, short names, badges only)
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/public/showcase`)
      .then((res) => {
        if (cancelled) return;
        setComp(res.data?.competition || null);
        setFeed(res.data?.feed || []);
      })
      .catch(() => {
        if (cancelled) return;
        setComp(null);
        setFeed([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const winners = comp?.status === 'finished' ? (comp.entries || []).filter((e) => e.rank).sort((a, b) => a.rank - b.rank) : [];
  const people = comp?.participants || [];
  const when =
    comp?.status === 'upcoming'
      ? `Starts ${day(comp.startDate)}`
      : comp?.status === 'open'
      ? `Open until ${day(comp.endDate)}`
      : comp?.status === 'judging'
      ? 'Teachers are choosing the winners'
      : comp
      ? `${day(comp.startDate, { month: 'long', year: 'numeric' })}`
      : '';

  return (
    <section className="rmdsc" aria-labelledby="rmdsc-title">
      <h2 id="rmdsc-title" className="rmdsc-title">
        Life at RoboMind
      </h2>
      <p className="rmdsc-sub">Monthly challenges, medals and badges: our students love to show what they build.</p>

      <div className="rmdsc-grid">
        {comp ? (
          <article className="rmdsc-comp">
            <span className="rmdsc-eyebrow">
              <FontAwesomeIcon icon={faTrophy} /> {comp.status === 'finished' ? 'Last challenge' : 'This month’s challenge'}
            </span>
            <h3>{comp.title}</h3>
            <p className="rmdsc-when">
              <FontAwesomeIcon icon={comp.status === 'open' ? faHourglassHalf : faCalendarDays} /> {when}
            </p>
            {comp.description && <p className="rmdsc-desc">{comp.description}</p>}

            {winners.length > 0 ? (
              <ul className="rmdsc-winners">
                {winners.map((w) => (
                  <li key={w.id} className={`is-${w.rank}`}>
                    <FontAwesomeIcon icon={w.rank === 1 ? faTrophy : faMedal} />
                    <b>{PLACE[w.rank]}</b> {w.by}
                    {w.title ? <span> · “{w.title}”</span> : null}
                  </li>
                ))}
              </ul>
            ) : people.length > 0 ? (
              <div className="rmdsc-people">
                <p>
                  <FontAwesomeIcon icon={faUsers} /> {people.length} {people.length === 1 ? 'student is' : 'students are'} taking part
                </p>
                <Participants list={people} max={8} compact />
              </div>
            ) : null}

            <a
              className="rmdsc-btn"
              href={waUrl(`Hi! I'd like to enroll ${firstName} so they can join the RoboMind challenges 🏆`)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faWhatsapp} /> Enroll {firstName} to join the challenges
            </a>
          </article>
        ) : comp === null ? (
          <article className="rmdsc-comp">
            <span className="rmdsc-eyebrow">
              <FontAwesomeIcon icon={faTrophy} /> Monthly challenges
            </span>
            <h3>A new challenge every month</h3>
            <p className="rmdsc-desc">Our students build a game, an app or an AI project around a theme, and the teachers pick the winners. Medals, badges and a lot of fun!</p>
            <a className="rmdsc-btn" href={waUrl(`Hi! I'd like to enroll ${firstName} so they can join the RoboMind challenges 🏆`)} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faWhatsapp} /> Enroll {firstName} to join
            </a>
          </article>
        ) : (
          <div className="rmdsc-skel" aria-hidden="true" />
        )}

        <WallOfFame items={feed || undefined} empty="Our students earn badges like Student of the month, Always on time and Creative mind. Soon it could be you! 🌟" />
      </div>
    </section>
  );
};

export default DemoShowcase;
